import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api/axiosConfig"; 
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";

const ComplianceContext = createContext();
export const useCompliance = () => useContext(ComplianceContext);

export const ComplianceProvider = ({ children }) => {
  const [compliances, setCompliances] = useState([]);
  
  const { user } = useAuth();

  // 1. FETCH RECORDS (GET from DB)
  const fetchCompliance = async () => {
    try {
      const res = await api.get("/compliance");
      setCompliances(res.data);
    } catch (err) {
      console.error("Error fetching compliance records:", err);
    }
  };

  useEffect(() => {
    if (user && user.id) {
      fetchCompliance();
    } else {
      setCompliances([]);
    }
  }, [user]);

  const formatForMySQL = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null;
    return d.toISOString().split('T')[0];
  };

  // 2. SAVE RECORD (POST or PUT)
  const saveCompliance = async (data) => {
    try {
      const formattedData = {
        ...data,
        issueDate: formatForMySQL(data.issueDate),
        expiryDate: formatForMySQL(data.expiryDate),
      };

      if (formattedData.id) {
        await api.put(`/compliance/${formattedData.id}`, formattedData);
        setCompliances((prev) =>
          prev.map((item) => String(item.id) === String(formattedData.id) ? { ...item, ...formattedData } : item)
        );
      } else {
        const res = await api.post("/compliance", formattedData);
        setCompliances((prev) => [res.data, ...prev]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save record");
    }
  };

  // 3. DELETE RECORD (DELETE)
  const deleteCompliance = async (id) => {
    try {
      await api.delete(`/compliance/${id}`);
      setCompliances((prev) => prev.filter((item) => String(item.id) !== String(id)));
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete record");
    }
  };

  // 4. GET SINGLE RECORD (From State)
  const getCompliance = (id) => {
      return compliances.find((item) => String(item.id) === String(id)) || null;
  };

  // 5. UPDATE STATUS ONLY (Helper)
  const updateComplianceStatus = async (id, status) => {
    try {
        const item = getCompliance(id);
        if(item) {
            await saveCompliance({ ...item, status });
        }
    } catch (err) {
        toast.error("Failed to update status");
    }
  };

  // 🟢 6. DEEP-LINK / DEDICATED FETCH FUNCTION
  const fetchComplianceById = React.useCallback(async (id) => {
    try {
      const res = await api.get(`/compliance/${id}`);
      return res.data?.data || res.data;
    } catch (err) {
      console.warn("fetchComplianceById fallback to local state:", err);
      return compliances.find(item => String(item.id) === String(id)) || null;
    }
  }, [compliances]);

  return (
    <ComplianceContext.Provider value={{
      compliances, 
      fetchCompliance, 
      saveCompliance, 
      deleteCompliance, 
      getCompliance, 
      updateComplianceStatus,
      fetchComplianceById // 🟢 Exported for the page
    }}>
      {children}
    </ComplianceContext.Provider>
  );
};